import { RootState } from 'index';
import React, { useEffect, useState } from 'react'
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { queryKeyes } from 'shared/queryKeys';
import { Get } from 'VesselMaster/vesselMaster.hooks';
import Loading from './Loading';
import ErrorComponent from './ErrorComponent';
import Message from './Message';
import { dateTimeFormat } from 'GenericForms/Helper';

interface EUMRVFuelEUTableType {
    eumrvData: any,
    operation: string,
    totalEUA?: number,
    setTotalEUA?: any
}

const EUMRVFuelEUTable = ({
    eumrvData,
    operation,
    setTotalEUA
}: EUMRVFuelEUTableType) => {
    /** State variables start */
    const { VesselID } = useSelector((state: RootState) => state.Reporting);
    const [consumptionEmmission, setConsumptionEmmission] = useState<Array<any>>([]);
    /** State varibles end */

    /** useQueries */
    /** Fetch Vessel distinct fuel  */
    const {
        data: fuelSubTypes,
        isLoading: fuelSubTypesLoading,
        isError: fuelSubTypesError,
    }: { data: any[]; isLoading: any; isError: any } = useQuery(
        [queryKeyes.vessel.fuel.key, VesselID],
        async () => {
            return await Get(queryKeyes.vessel.vesselFuel.url(VesselID), VesselID);
        },
        { staleTime: Infinity }
    );
    /** useQueries end */

    /** useEffect */
    useEffect(() => {
        let array: any[] = []
        if (operation === 'FuelEU') {
            array.push('Fuel Consumption');
        } else {
            array.push('Fuel Consumption', 'CO2', 'CH4', 'N2O');
        }
        setConsumptionEmmission(array);
    }, [operation])
    useEffect(() => {
        if (operation === 'EUMRV') {
            let total = 0;
            eumrvData?.forEach((item: any) => {
                total += parseFloat((item?.eua));
            })
            setTotalEUA(total?.toFixed(0));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eumrvData])
    /** useEffect end */

    return (
        <React.Fragment>
            {fuelSubTypesLoading && <Loading message='Loading required data!' />}
            {fuelSubTypesError && <ErrorComponent message='Error loading required data!' />}
            {(!fuelSubTypesLoading && !fuelSubTypesError) &&
                <div className="table-responsive vertical-table-scroll">
                    {eumrvData && eumrvData?.length > 0 && eumrvData[0]?.fuel_consumption && eumrvData[0]?.fuel_consumption?.length > 0 ?
                        <table className="table m-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="text-center p-2">#</th>
                                    <th className='text-center p-2'>Start date</th>
                                    <th className='text-center p-2'>End date</th>
                                    <th className='text-center p-2'>Departure port</th>
                                    <th className='text-center p-2'>Arrival port</th>
                                    <th className='text-center p-2' colSpan={fuelSubTypes?.length}>Fuel consumption</th>
                                    {operation === 'EUMRV' &&
                                        <React.Fragment>
                                            <th className='text-center p-2' colSpan={fuelSubTypes?.length}>CO<sub>2</sub> emmission</th>
                                            <th className='text-center p-2' colSpan={fuelSubTypes?.length}>CH<sub>4</sub> emmission</th>
                                            <th className='text-center p-2' colSpan={fuelSubTypes?.length}>N<sub>2</sub>O emmission</th>
                                        </React.Fragment>
                                    }
                                    <th className='text-center p-2'>Applicability</th>
                                    {operation === 'EUMRV' &&
                                        <th className='text-center p-2'>Emmission applicable to EU</th>
                                    }
                                    {operation === 'EUMRV' ?
                                        <th className='text-center p-2'>EUA</th> :
                                        <React.Fragment>
                                            <th className='text-center p-2'>GHGIE</th>
                                            <th className='text-center p-2'>CB</th>
                                            <th className='text-center p-2'>Penalty</th>
                                        </React.Fragment>
                                    }
                                </tr>
                                <tr>
                                    <th colSpan={5}></th>
                                    {consumptionEmmission?.map((item: any) => (
                                        <React.Fragment key={item}>
                                            {fuelSubTypes?.sort((a: any, b: any) => a.fuel_type_precedence_id - b.fuel_type_precedence_id)?.map((fuel: any) => {
                                                return (
                                                    <th key={fuel.id} className='text-center p-2'>
                                                        {fuel?.fuel_sub_type_short_name}
                                                    </th>
                                                )
                                            })}
                                        </React.Fragment>
                                    ))}
                                    <th colSpan={fuelSubTypes?.length > 0 ? ((fuelSubTypes?.length * 4) + 3) : 7} />
                                </tr>
                            </thead>
                            <tbody>
                                {eumrvData && eumrvData?.length > 0 && eumrvData[0]?.fuel_consumption && eumrvData[0]?.fuel_consumption?.length > 0 &&
                                    (eumrvData?.map((item: any, index: number) => {
                                        return (
                                            <tr>
                                                <td className='text-center p-2 align-middle'>{index + 1}</td>
                                                <td className='text-center p-2 align-middle'>{dateTimeFormat(item?.start_date, false, false, true)}</td>
                                                <td className='text-center p-2 align-middle'>{dateTimeFormat(item?.end_date, false, false, true)}</td>
                                                <td className='text-center p-2 align-middle'>{item?.departure_port === "" ? "-" : item?.departure_port}</td>
                                                <td className='text-center p-2 align-middle'>{item?.arrival_port === "" ? "-" : item?.arrival_port ?? "-"}</td>
                                                {fuelSubTypes?.sort((a: any, b: any) => a.fuel_type_precedence_id - b.fuel_type_precedence_id)?.map((fuel: any) => {
                                                    return (
                                                        <td className='text-center p-2 align-middle' key={fuel}>{
                                                            item?.fuel_consumption?.find((item: any) =>
                                                                item.fuel_sub_type_id === fuel.fuel_sub_type)?.fuel_consumption?.toFixed(2) ?? "-"
                                                        }</td>
                                                    )
                                                })}
                                                {operation === 'EUMRV' &&
                                                    <React.Fragment>
                                                        {fuelSubTypes?.sort((a: any, b: any) => a.fuel_type_precedence_id - b.fuel_type_precedence_id)?.map((fuel: any) => {
                                                            return (
                                                                <td className='text-center p-2 align-middle' key={fuel}>{
                                                                    item?.co2_emission?.find((item: any) =>
                                                                        item.fuel_sub_type_id === fuel.fuel_sub_type)?.co2_emission?.toFixed(2) ?? "-"
                                                                }</td>
                                                            )
                                                        })}
                                                        {fuelSubTypes?.sort((a: any, b: any) => a.fuel_type_precedence_id - b.fuel_type_precedence_id)?.map((fuel: any) => {
                                                            return (
                                                                <td className='text-center p-2 align-middle' key={fuel}>{
                                                                    item?.ch4_emission?.find((item: any) =>
                                                                        item.fuel_sub_type_id === fuel.fuel_sub_type)?.ch4_emission?.toFixed(2) ?? "-"
                                                                }</td>
                                                            )
                                                        })}
                                                        {fuelSubTypes?.sort((a: any, b: any) => a.fuel_type_precedence_id - b.fuel_type_precedence_id)?.map((fuel: any) => {
                                                            return (
                                                                <td className='text-center p-2 align-middle' key={fuel}>{
                                                                    item?.n2o_emission?.find((item: any) =>
                                                                        item.fuel_sub_type_id === fuel.fuel_sub_type)?.n2o_emission?.toFixed(2) ?? "-"
                                                                }</td>
                                                            )
                                                        })}
                                                    </React.Fragment>
                                                }
                                                <td className='text-center p-2 align-middle'>{item?.applicability} %</td>
                                                {operation === 'EUMRV' &&
                                                    <td className='text-center p-2 align-middle'>{item?.emission_applicable_to_eu}</td>
                                                }
                                                {operation === 'EUMRV' ?
                                                    <td className='text-center p-2 align-middle'>{item?.eua}</td> :
                                                    <React.Fragment>
                                                        <td className='text-center p-2 align-middle'>{item?.fuel_eu_record?.final_GHG_intensity?.toFixed(2)}</td>
                                                        <td className='text-center p-2 align-middle'>{item?.fuel_eu_record?.Compliance_Balance}</td>
                                                        <td className='text-center p-2 align-middle'>€ {item?.fuel_eu_record?.penalty}</td>
                                                    </React.Fragment>
                                                }
                                            </tr>
                                        )
                                    }))
                                }
                            </tbody>
                        </table>
                        :
                        <Message message='No data available!' type='danger' width='365px' />
                    }
                </div>
            }
        </React.Fragment>
    )
}

export default EUMRVFuelEUTable